import shaka from 'shaka-player';
import Logger from '../../utils/logger';
import DAIReporter from './reporter';
import { parseTime } from '../../players/shaka/ttml-parser';

const isMatchingAd = (ad, currentTime) => currentTime >= ad.absStartTime && currentTime <= ad.absEndTime;
const isTimeEqual = (first, second) => Math.floor(Math.abs(first - second)) === 0;

// set ad index like `{index}/{total}` -> '2/5'
const setAdsIndex = (avail, ads) => {
  const firstAd = ads[0];
  const lastAd = ads[ads.length - 1];

  // set ad index only if avail is fully filled
  if (firstAd) {
    const availEndTime = avail.startTimeInSeconds + avail.durationInSeconds;
    const lastAdEndTime = lastAd.startTimeInSeconds + lastAd.durationInSeconds;

    if (isTimeEqual(avail.startTimeInSeconds, firstAd.startTimeInSeconds)
      && isTimeEqual(availEndTime, lastAdEndTime)
    ) {
      ads.forEach((ad, i) => {
        ad.index = `${i + 1}/${ads.length}`;
      });
    }
  }
};

// live ad index = 1, 2, 3, ...
const setLiveAdsIndex = (ads) => {
  ads.forEach((ad, i) => {
    if (!ad.index) {
      let index = '1';
      const prevAd = ads[i - 1];

      if (prevAd) {
        const prevAdEndTime = prevAd.startTimeInSeconds + prevAd.durationInSeconds;
        if (isTimeEqual(prevAdEndTime, ad.startTimeInSeconds)) {
          index = `${parseInt(prevAd.index, 10) + 1}`;
        }
      }

      ad.index = index;
    }
  });
};

const updateTrackingEvents = (events, newOne) => {
  let amount = 0;
  newOne.forEach((ne) => {
    if (!events.find(e => e.eventType === ne.eventType)) {
      events.push(ne);
      amount += 1;
    }
  });
  return amount;
};

export default class DAITrackingService {
  _player = null;
  _state = null;
  _reporter = null;

  constructor(player) {
    this._player = player;
    this._reporter = new DAIReporter(player);
  }

  async start(initMediaTailorUrl, adsParams) {
    const {
      manifestUrl,
      trackingUrl,
    } = await this._initMediaTailor(initMediaTailorUrl, adsParams);

    this._state = {
      trackingUrl,
      ads: [],
      nextToken: undefined,
      ad: null,
      firstPeriodDuration: 0,
    };

    this._player.addEventListener('manifestwillupdate', this._onManifestWillUpdate);
    this._player.addEventListener('ontimeupdate', this._onTimeupdate);
    this._player.addEventListener('seeked', this._onSeeked);
    this._player.addEventListener('playerisinitialized', this._onPlayerInitialized);

    Logger.debug('DAI tracking service: started');

    return manifestUrl;
  }

  stop() {
    if (!this._state) {
      return;
    }

    if (this._state.ad) {
      this._player.dispatchEvent('dai:ended');
    }

    this._reporter.stop();

    this._state = null;
    this._player.removeEventListener('manifestwillupdate', this._onManifestWillUpdate);
    this._player.removeEventListener('ontimeupdate', this._onTimeupdate);
    this._player.removeEventListener('seeked', this._onSeeked);
    this._player.removeEventListener('playerisinitialized', this._onPlayerInitialized);

    Logger.debug('DAI tracking service: stopped');
  }

  // set firstPeriodDuration from manifest
  // this value will be used to find proper duration of first ad
  _onPlayerInitialized = () => {
    this._player.currentPlayer?.videoPlayer?.getNetworkingEngine()
      .registerResponseFilter((type, response) => {
        if (type === shaka.net.NetworkingEngine.RequestType.MANIFEST && !this._state.firstPeriodDuration) {
          const decoder = new TextDecoder('utf-8');
          const xmlParser = new DOMParser();
          const manifest = xmlParser.parseFromString(decoder.decode(response.data), 'application/xml');
          const periods = manifest.querySelectorAll('Period');

          if (periods.length >= 2) {
            this._state.firstPeriodDuration = parseTime(
              periods[1]
                .getAttribute('start')
                .replace('PT', '')
                .toLowerCase(),
            ) || 0;
          }
        }
      });
  };

  async _initMediaTailor(initMediaTailorUrl, adsParams) {
    // init media tailor playback session
    // step 2 from the https://docs.aws.amazon.com/mediatailor/latest/ug/ad-reporting-client-side.html
    const {
      manifestUrl,
      trackingUrl,
    } = await fetch(initMediaTailorUrl, {
      method: 'POST',
      mode: 'cors',
      body: JSON.stringify({ adsParams }),
    }).then(result => result.json());

    const url = new URL(initMediaTailorUrl);
    return {
      manifestUrl: url.origin + manifestUrl,
      trackingUrl: url.origin + trackingUrl,
    };
  }

  _onManifestWillUpdate = async () => {
    const {
      nextToken,
      avails,
    } = await this._fetchAds().catch((e) => {
      Logger.warn('DAI tracking service: failed to fetch ads', e);

      return {};
    });

    // service might be stopped while we're fetching ads
    if (!this._state || !avails) {
      return;
    }

    this._state.nextToken = nextToken;

    // add new ads
    const knownAds = this._state.ads;
    const seekRangeStart = this._player.model.seekRange.startAbsolute;

    let newAdsCount = 0;

    for (const avail of avails) {
      if (!this._player.model.isLive) {
        setAdsIndex(avail, avail.ads);
      }

      for (const ad of avail.ads) {
        const knownAd = knownAds.find(item => item.adId === ad.adId);
        if (knownAd) {
          // duration of live dai ad changes during playback
          if (knownAd.durationInSeconds !== ad.durationInSeconds) {
            knownAd.absEndTime = knownAd.absStartTime + ad.durationInSeconds;
          }

          // check if we got new events. add them to an ad
          const updated = updateTrackingEvents(knownAd.trackingEvents, ad.trackingEvents);
          if (updated && knownAd === this._state.ad) {
            // run new events if ad is playing
            this._reporter.startNewEvents(knownAd);
          }
          continue;
        }

        // convert relative start time to absolute start time
        ad.absStartTime = this._toAbsoluteTime(ad.startTimeInSeconds);
        ad.absEndTime = ad.absStartTime + ad.durationInSeconds;

        // first ad can be present only partially
        // it happens when avail starts in prev VOD and finish in current one
        // we can find this only by periods in manifest
        // in this case end time is incorrect
        if (
          !this._player.model.isLive
          && !ad.startTimeInSeconds
          && this._state.firstPeriodDuration
          && this._state.firstPeriodDuration < ad.durationInSeconds
        ) {
          ad.absEndTime = this._state.firstPeriodDuration;
        }

        if (ad.absEndTime >= seekRangeStart) {
          knownAds.push(ad);
          newAdsCount += 1;
        }
      }
    }
    if (newAdsCount) {
      Logger.info(`DAI tracking service: got ${newAdsCount} new ads`);
      setLiveAdsIndex(knownAds);
    }

    // remove ads older than playback window
    const knownAdsCount = knownAds.length;
    this._state.ads = knownAds.filter(avail => avail.absEndTime > seekRangeStart);
    const removedAdsCount = knownAdsCount - this._state.ads.length;
    if (removedAdsCount) {
      Logger.info(`DAI tracking service: removed ${removedAdsCount} old ads`);
    }

    if (newAdsCount) {
      this._player.dispatchEvent('dai:allAds', [...this._state.ads]);
    }
  }

  async _fetchAds() {
    const {
      trackingUrl,
      nextToken,
    } = this._state;

    // pass nextToken if available to fetch only new ads
    const response = await fetch(trackingUrl + (nextToken ? `?aws.nextToken=${nextToken}` : ''));
    if (response.ok) {
      return response.json();
    }

    const body = await response.text();

    throw new Error(`Bad Response: ${response.status} ${response.statusText}\n${body}`);
  }

  _toAbsoluteTime(timestamp) {
    return this._player._shakaPlayer.playerTimeToAbsoluteTimeFloat(timestamp);
  }

  _onSeeked = () => {
    this.updateAdState(true);
  };

  _onTimeupdate = () => {
    this.updateAdState();
  };

  updateAdState(afterSeek = false) {
    const {
      currentTimeFloat,
    } = this._player.model;

    // keep playing the same ad
    if (this._state.ad && isMatchingAd(this._state.ad, currentTimeFloat)) {
      return;
    }

    const ad = this._state.ads.find(item => isMatchingAd(item, currentTimeFloat));

    // started playing an ad
    if (ad && !this._state.ad) {
      this._state.ad = ad;
      Logger.info('DAI tracking service: playing ad', afterSeek ? 'after seek' : '', ad);
      this._reporter.updateAd(ad);
      this._player.dispatchEvent('dai:playing', { ad, afterSeek });
      return;
    }

    // ad changed
    if (ad && this._state.ad && ad.adId !== this._state.ad.adId) {
      this._state.ad = ad;
      Logger.info('DAI tracking service: ad changed, playing ad', afterSeek ? 'after seek' : '', ad);
      this._reporter.updateAd(ad);
      this._player.dispatchEvent('dai:playing', { ad, afterSeek });
      return;
    }

    // finished playing an ad
    if (!ad && this._state.ad) {
      Logger.info('DAI tracking service: ad ended', afterSeek ? 'after seek' : '');
      this._reporter.updateAd(null);
      this._player.dispatchEvent('dai:ended', { afterSeek });

      this._state.ad = null;
      return; // eslint-disable-line no-useless-return
    }
  }
}
